import { ConfirmModal, Loading, Table, TableRowMenuCell } from '@/components';
import { useMasterTablePermission } from '@/features/authentication/_hooks/permission';
import { useMessage } from '@/providers';
import {
  TABLE_CODE_WIDTH,
  TABLE_DATETIME_WIDTH,
  TABLE_ROW_MENU_WIDTH,
  TABLE_YEAR_WIDTH,
  datetimeFormat,
} from '@/utils';
import dayjs from 'dayjs';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import {
  useCreateExpenditureBudgetOrganization,
  useDeleteExpenditureBudgetOrganization,
  useExpenditureBudgetOrganizations,
  useUpdateExpenditureBudgetOrganization,
} from '../../hooks';
import type { ExpenditureBudgetOrganizationModel } from '../../models';
import {
  DEFAULT_PAGINATION_SETTING,
  fillZeroToExpenditureBudgetOrganization,
} from '../../utils';
import { useDrawerState } from '../DrawerStateProvider';
import { ExpenditureBudgetOrganizationFormDrawer } from './ExpenditureBudgetOrganizationFormDrawer';

export const ExpenditureBudgetOrganizationsPage: FC = () => {
  const { t } = useTranslation();
  const { drawerState, setDrawerState } = useDrawerState();
  const { openMessage } = useMessage();
  const organizations = useExpenditureBudgetOrganizations({});
  const { mutateAsync: createMutateAsync } =
    useCreateExpenditureBudgetOrganization();
  const { mutateAsync: updateMutateAsync } =
    useUpdateExpenditureBudgetOrganization();
  const { mutateAsync: deleteMutateAsync } =
    useDeleteExpenditureBudgetOrganization();
  const editPermission = useMasterTablePermission(true);
  const [
    expenditureBudgetOrganizationDelete,
    setExpenditureBudgetOrganizationDelete,
  ] = useState<z.infer<typeof ExpenditureBudgetOrganizationModel> | null>(null);

  if (organizations.isLoading || !organizations.data) {
    return <Loading />;
  }

  const selected = organizations.data.find(
    (d) =>
      drawerState.open &&
      drawerState.type === 'expenditure-budget-organization' &&
      drawerState.isEditing &&
      d.id === drawerState.id,
  );

  return (
    <>
      <Table
        dataSource={organizations.data}
        rowKey="id"
        columns={[
          {
            title: t('code'),
            key: 'code',
            dataIndex: 'code',
            width: TABLE_CODE_WIDTH,
            render: fillZeroToExpenditureBudgetOrganization,
          },
          {
            title: t('name'),
            key: 'name',
            dataIndex: 'name',
          },
          {
            title: t('start-fiscal-year'),
            key: 'startYear',
            dataIndex: 'startYear',
            width: TABLE_YEAR_WIDTH,
          },
          {
            title: t('end-fiscal-year'),
            key: 'endYear',
            dataIndex: 'endYear',
            width: TABLE_YEAR_WIDTH,
          },
          {
            title: t('created-datetime'),
            key: 'createdAt',
            dataIndex: 'createdAt',
            width: TABLE_DATETIME_WIDTH,
            render: (value) => datetimeFormat({ datetime: value }),
          },
          {
            title: t('updated-datetime'),
            key: 'updatedAt',
            dataIndex: 'updatedAt',
            width: TABLE_DATETIME_WIDTH,
            render: (value) => datetimeFormat({ datetime: value }),
          },
          ...(editPermission.data
            ? [
                {
                  title: '',
                  key: 'actions',
                  fixed: 'right' as const,
                  width: TABLE_ROW_MENU_WIDTH,
                  onCell: () => ({
                    style: { minWidth: TABLE_ROW_MENU_WIDTH },
                  }),
                  render: (_, record) => (
                    <TableRowMenuCell
                      onEdit={() => {
                        setDrawerState({
                          open: true,
                          type: 'expenditure-budget-organization',
                          isEditing: true,
                          id: record.id,
                        });
                      }}
                      items={[
                        {
                          key: 'delete',
                          label: t('delete'),
                          danger: true,
                          onClick: () =>
                            setExpenditureBudgetOrganizationDelete(record),
                        },
                      ]}
                    />
                  ),
                },
              ]
            : []),
        ]}
        pagination={DEFAULT_PAGINATION_SETTING}
      />

      <ExpenditureBudgetOrganizationFormDrawer
        {...(drawerState.open && drawerState.isEditing
          ? {
              type: 'edit',
              initialValues: {
                ...selected,
                period: [
                  selected?.startYear
                    ? dayjs().year(selected.startYear)
                    : undefined,
                  selected?.endYear
                    ? dayjs().year(selected.endYear)
                    : undefined,
                ],
              },
            }
          : { type: 'new' })}
        onSubmit={async (values) => {
          invariant(drawerState.open, t('unreachable-code'));

          drawerState.isEditing
            ? await updateMutateAsync({ ...values, id: drawerState.id })
            : await createMutateAsync(values);

          await Promise.all([
            openMessage(
              'success',
              drawerState.isEditing
                ? t('message-update-completed')
                : t('message-create-completed'),
            ),
            organizations.refetch(),
            (async () => {
              setDrawerState({ open: false });
            })(),
          ]);
        }}
      />
      <ConfirmModal
        open={expenditureBudgetOrganizationDelete !== null}
        title={t('delete-confirmation')}
        disclaimerText={t('delete-confirmation-disclaimer')}
        onOk={async () => {
          invariant(expenditureBudgetOrganizationDelete, t('unreachable-code'));
          try {
            await deleteMutateAsync(expenditureBudgetOrganizationDelete.id);
            await Promise.all([
              openMessage('success', t('message-delete-completed')),
              (async () => {
                setExpenditureBudgetOrganizationDelete(null);
              })(),
            ]);
          } catch {
            await openMessage('error', t('message-delete-failed'));
          }
        }}
        onCancel={() => setExpenditureBudgetOrganizationDelete(null)}
        skipCheck
      />
    </>
  );
};
